// Calendar math for the Logs timeline, in the timeZone the relay bucketed the days in.
// Pure: every function takes the zone, so tests pin one and never read the machine's.
import type { LogsDayKey } from "./wire.js";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const formatters = new Map<string, Intl.DateTimeFormat>();

// ICU puts a narrow no-break space before AM/PM on some runtimes; print a plain one.
function plain(text: string): string {
  return text.replace(/[\u202f\u00a0]/g, " ");
}

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone}|${JSON.stringify(options)}`;
  let fmt = formatters.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", { timeZone, ...options });
    formatters.set(key, fmt);
  }
  return fmt;
}

/** The browser's IANA zone; "UTC" when the runtime cannot say. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function wallParts(ms: number, timeZone: string): Record<string, number> {
  const parts = formatter(timeZone, {
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const out: Record<string, number> = {};
  for (const part of parts) if (part.type !== "literal") out[part.type] = Number(part.value);
  return out;
}

// How far the zone's wall clock is ahead of UTC at `ms`.
function zoneOffsetMs(ms: number, timeZone: string): number {
  const p = wallParts(ms, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** "2026-09-21" for the calendar day `ms` falls on in `timeZone`. */
export function dayKeyOf(ms: number, timeZone: string): LogsDayKey {
  const p = wallParts(ms, timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

function parseDayKey(day: LogsDayKey): [number, number, number] {
  const [y, m, d] = day.split("-").map(Number);
  return [y, m, d];
}

/** Epoch ms of local midnight that starts `day` in `timeZone` (DST-safe). */
export function dayStartMs(day: LogsDayKey, timeZone: string): number {
  const [y, m, d] = parseDayKey(day);
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - zoneOffsetMs(guess, timeZone);
  return guess - zoneOffsetMs(first, timeZone);
}

export function addDays(day: LogsDayKey, n: number): LogsDayKey {
  const [y, m, d] = parseDayKey(day);
  const t = new Date(Date.UTC(y, m - 1, d) + n * DAY_MS);
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/** Whole calendar days from `from` to `to` (both "YYYY-MM-DD"). */
export function daysBetween(from: LogsDayKey, to: LogsDayKey): number {
  const [y1, m1, d1] = parseDayKey(from);
  const [y2, m2, d2] = parseDayKey(to);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY_MS);
}

/** "Sep 19", or "Sep 19, 2025" outside the current year. */
export function shortDate(day: LogsDayKey, today: LogsDayKey): string {
  const [y, m, d] = parseDayKey(day);
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** "Today", "Yesterday", else "Sep 19". */
export function dayLabel(day: LogsDayKey, today: LogsDayKey): string {
  if (day === today) return "Today";
  if (day === addDays(today, -1)) return "Yesterday";
  return shortDate(day, today);
}

/** "4:16 PM" */
export function timeOfDay(ms: number, timeZone: string): string {
  return plain(formatter(timeZone, { hour: "numeric", minute: "2-digit" }).format(new Date(ms)));
}

/** "Wed", the weekday `ms` falls on in `timeZone`. */
export function weekdayShort(ms: number, timeZone: string): string {
  return formatter(timeZone, { weekday: "short" }).format(new Date(ms));
}

/** "4:16:02 PM", for the technical rows. */
export function timeWithSeconds(ms: number, timeZone: string): string {
  return plain(
    formatter(timeZone, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(
      new Date(ms),
    ),
  );
}

/** "9 AM", for the drop strip's axis. */
export function hourLabel(ms: number, timeZone: string): string {
  return plain(formatter(timeZone, { hour: "numeric" }).format(new Date(ms)));
}

/** The start of the local hour `ms` falls in (half-hour zones included). */
export function hourFloorMs(ms: number, timeZone: string): number {
  const start = dayStartMs(dayKeyOf(ms, timeZone), timeZone);
  return ms - ((ms - start) % HOUR_MS);
}

export { HOUR_MS, DAY_MS };
